import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { useTypedSelector } from '../../../shared/hooks';
import AlertService from '../../../shared/services/AlertService';
import { alertQueueActions } from '../../../shared/store/alertQueueSlice';
import {
  DEFAULT_EXPORT_POLLING_INTERVAL_MS,
  resolveExportPollingIntervalMs,
} from '../../../shared/utils/exportPollingInterval';
import getUsername from '../../../shared/utils/getUsername';
import formatReportType from '../../../shared/utils/reportType';
import { useLazyGetExportStatusQuery } from '../data-layer/violationsReportApi';
import {
  type ExportStatusItem,
  type GetExportStatusData,
} from '../data-layer/violationsReportTypes';
import { violationsReportActions } from '../data-store/violationsReportSlice';

const useExportStatusPolling = () => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollExportStatusRef = useRef<() => Promise<void>>(async () => {});
  const shownFailureBannersRef = useRef<Set<string>>(new Set());
  const shownSuccessBannersRef = useRef<Set<string>>(new Set());
  const { activeExports } = useTypedSelector((state) => state.aag.report);
  const [getExportStatus] = useLazyGetExportStatusQuery();

  const getCurrentUserId = useCallback(() => getUsername(), []);

  const showSuccessBanner = useCallback((reportType: string) => {
    // De-duplicate by reportType only
    const key = reportType;

    if (shownSuccessBannersRef.current.has(key)) {
      return;
    }

    shownSuccessBannersRef.current.add(key);

    // Suppress top success banner to avoid duplication with stacked banners
  }, []);

  const showErrorBanner = useCallback(
    (reportType: string, exportId?: string, errorMessage?: string) => {
      // De-duplicate by reportType only
      const key = reportType;

      if (shownFailureBannersRef.current.has(key)) {
        return;
      }

      shownFailureBannersRef.current.add(key);

      const description =
        errorMessage ??
        `${formatReportType(reportType)} export has failed. Please try again.`;

      dispatch(
        alertQueueActions.enqueue(
          AlertService.error(
            'Export failed',
            description,
            5000,
            `vio-export-error-${reportType}`
          )
        )
      );
    },
    [dispatch]
  );

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    dispatch(violationsReportActions.setIsPolling(false));

    // Clear the banners tracking when polling stops
    shownFailureBannersRef.current.clear();
    shownSuccessBannersRef.current.clear();
  }, [dispatch]);

  const scheduleNextPoll = useCallback((delayMs: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      pollExportStatusRef
        .current()
        .catch((error) => Logger.log('error', 'Polling failed:', error));
    }, delayMs);
  }, []);

  const pollExportStatus = useCallback(async () => {
    try {
      const userId = getCurrentUserId();
      const response: GetExportStatusData = await getExportStatus(
        userId
      ).unwrap();

      // Filter exports to only process VIO report types
      const vioReportTypes = ['SUMMARY', 'DETAIL', 'PERMISSIONS'];
      const vioExports: ExportStatusItem[] = response.exports.filter(
        (exportItem) => vioReportTypes.includes(exportItem.reportType)
      );

      // First, process each export status to show appropriate banners
      const seenReportTypes: string[] = [];

      vioExports.forEach((exportItem: ExportStatusItem) => {
        const {
          reportType,
          status,
          exportId,
          progressPercentage,
          errorMessage,
        } = exportItem;

        // Update progress for this report type
        dispatch(
          violationsReportActions.setExportProgress({
            reportType,
            progressPercentage,
          })
        );

        if (!seenReportTypes.includes(reportType)) {
          seenReportTypes.push(reportType);
        }

        if (status === 'COMPLETED') {
          // Show success banner for this specific reportType and store exportId for download
          showSuccessBanner(reportType);
          dispatch(
            violationsReportActions.setExportIdForReportType({
              reportType,
              exportId,
              status,
            })
          );
          dispatch(violationsReportActions.removeActiveExport(reportType));
        } else if (status === 'FAILED') {
          showErrorBanner(reportType, exportId, errorMessage);

          if (exportId) {
            dispatch(
              violationsReportActions.setExportIdForReportType({
                reportType,
                exportId,
                status,
              })
            );
          }

          dispatch(violationsReportActions.removeActiveExport(reportType));
        }
      });

      // Check if there are any VIO exports still in progress
      const vioInProgressExports: ExportStatusItem[] = vioExports.filter(
        (exportItem) =>
          exportItem.status === 'PROCESSING' || exportItem.status === 'QUEUED'
      );

      const shouldStopPolling =
        response.inProgressExports === 0 || vioInProgressExports.length === 0;

      // After processing statuses, decide whether to stop polling
      if (shouldStopPolling) {
        // Ensure local state is consistent so effect doesn't immediately restart polling
        dispatch(violationsReportActions.clearActiveExports());
        stopPolling();
      }

      // If API reports no completed exports, clear completed data so stacked success banners disappear
      if (response.completedExports === 0) {
        dispatch(violationsReportActions.clearCompletedExports());
      }

      // Track completed exports count for UI logic
      dispatch(
        violationsReportActions.setCompletedExportsCount(
          response.completedExports
        )
      );

      // Track report types present in response to gate visible banners
      dispatch(
        violationsReportActions.setLastStatusReportTypes(seenReportTypes)
      );

      if (!shouldStopPolling) {
        scheduleNextPoll(resolveExportPollingIntervalMs(vioInProgressExports));
      }
    } catch (error) {
      Logger.log('error', 'Failed to poll export status:', error);
      scheduleNextPoll(DEFAULT_EXPORT_POLLING_INTERVAL_MS);
    }
  }, [
    getCurrentUserId,
    getExportStatus,
    showSuccessBanner,
    showErrorBanner,
    dispatch,
    stopPolling,
    scheduleNextPoll,
  ]);

  pollExportStatusRef.current = pollExportStatus;

  const startPolling = useCallback(() => {
    if (timeoutRef.current) {
      return; // Already polling
    }

    scheduleNextPoll(DEFAULT_EXPORT_POLLING_INTERVAL_MS);
  }, [scheduleNextPoll]);

  // Check for existing exports on initial load
  useEffect(() => {
    const checkInitialExports = async () => {
      try {
        const userId = getCurrentUserId();
        const response: GetExportStatusData = await getExportStatus(
          userId
        ).unwrap();

        // Set completed exports count on initial check
        dispatch(
          violationsReportActions.setCompletedExportsCount(
            response.completedExports
          )
        );

        // If none completed, clear any completed data leftover
        if (response.completedExports === 0) {
          dispatch(violationsReportActions.clearCompletedExports());
        }

        // Filter exports to only process VIO report types
        const vioReportTypes = ['SUMMARY', 'DETAIL', 'PERMISSIONS'];
        const vioExports = response.exports.filter((exportItem) =>
          vioReportTypes.includes(exportItem.reportType)
        );

        // If there are VIO exports in progress, start polling
        const vioInProgressExports = vioExports.filter(
          (exportItem) =>
            exportItem.status === 'PROCESSING' || exportItem.status === 'QUEUED'
        );

        if (vioInProgressExports.length > 0) {
          // Add active VIO exports to Redux for tracking
          vioInProgressExports.forEach((exportItem: ExportStatusItem) => {
            dispatch(
              violationsReportActions.addActiveExport({
                reportType: exportItem.reportType,
                progress: exportItem.progressPercentage,
              })
            );
          });
        } else {
          dispatch(violationsReportActions.clearActiveExports());
          stopPolling();
        }
      } catch (error) {
        Logger.log('error', 'Failed to check initial exports:', error);
      }
    };

    checkInitialExports().catch((error) =>
      Logger.log('error', 'Initial export check failed:', error)
    );
  }, [dispatch, getCurrentUserId, getExportStatus, stopPolling]);

  // Start/stop polling based on active exports from Redux
  useEffect(() => {
    if (activeExports.length > 0) {
      dispatch(violationsReportActions.setIsPolling(true));
      startPolling();
    } else {
      stopPolling();
    }
  }, [activeExports, startPolling, stopPolling, dispatch]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );
};

export default useExportStatusPolling;
